/**
 * Graph Service
 *
 * Frontend API client for canonical experience graph endpoints.
 */

import { getFunctions } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getEmulatorHost, isUsingEmulators } from './emulator';
import {
  ExperienceGraph,
  GraphNeighborhood,
  GraphNodeType,
  GroupedRetrievalResult,
} from '../types/graph';

function shouldForceTokenRefresh(): boolean {
  return isUsingEmulators();
}

function getFunctionsBaseUrl(functions: ReturnType<typeof getFunctions>): string {
  const projectId =
    functions.app.options.projectId ||
    process.env.REACT_APP_FIREBASE_PROJECT_ID ||
    'postmortem-pheonix';
  const region = functions.region || 'us-central1';

  if (isUsingEmulators()) {
    const host = getEmulatorHost();
    const port = process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT || '5001';
    return `http://${host}:${port}/${projectId}/${region}`;
  }

  return functions.customDomain || `https://${region}-${projectId}.cloudfunctions.net`;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Must be authenticated');
  const idToken = await user.getIdToken(shouldForceTokenRefresh());
  return {
    'Authorization': `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Fetch the full experience graph for the current user.
 */
export async function fetchExperienceGraph(): Promise<ExperienceGraph> {
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);
  const headers = await getAuthHeaders();

  const response = await fetch(`${baseUrl}/getExperienceGraph`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch graph (${response.status})`);
  }

  return response.json();
}

/**
 * Fetch the 1-hop neighborhood around a specific node.
 */
export async function fetchGraphNeighborhood(
  nodeType: GraphNodeType,
  nodeId: string
): Promise<GraphNeighborhood> {
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);
  const headers = await getAuthHeaders();

  const params = new URLSearchParams({ nodeType, nodeId });
  const response = await fetch(`${baseUrl}/getGraphNeighborhood?${params}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch neighborhood (${response.status})`);
  }

  return response.json();
}

/**
 * Fetch grouped activities scored against job keywords.
 */
export async function fetchGroupedActivities(
  jobKeywords: string[],
  requiredKnowledge?: string[],
  maxActivities?: number
): Promise<GroupedRetrievalResult> {
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);
  const headers = await getAuthHeaders();

  const response = await fetch(`${baseUrl}/getGroupedActivities`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ jobKeywords, requiredKnowledge, maxActivities }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch grouped activities (${response.status})`);
  }

  return response.json();
}

/**
 * Trigger graph ingestion from the user's current resume.
 */
export async function ingestCurrentResume(): Promise<{
  message: string;
  totalCandidates: number;
  created: number;
  merged: number;
}> {
  const functions = getFunctions();
  const baseUrl = getFunctionsBaseUrl(functions);
  const headers = await getAuthHeaders();

  const response = await fetch(`${baseUrl}/ingestCurrentResume`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Failed to ingest resume (${response.status})`);
  }

  return response.json();
}
